import { FileType, FolderType, Pagination } from '../types';

const CACHE_KEY_PREFIX = 'library_cache_';
const FOLDERS_CACHE_KEY = 'library_folders_cache';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHED_FOLDERS = 10;

interface CachedFilesData {
  files: FileType[];
  pagination: Pagination;
  librarySize: number;
  timestamp: number;
}

interface CachedFoldersData {
  folders: FolderType[];
  timestamp: number;
}

interface CacheMetadata {
  keys: string[];
  timestamps: { [key: string]: number };
}

const getCacheMetadataKey = () => `${CACHE_KEY_PREFIX}metadata`;

const getCacheKey = (teamId: string, folderId: string) =>
  `${CACHE_KEY_PREFIX}${teamId}_${folderId}`;

const getCacheMetadata = (): CacheMetadata => {
  try {
    const metadata = localStorage.getItem(getCacheMetadataKey());
    if (metadata) {
      return JSON.parse(metadata);
    }
  } catch (e) {
    console.warn('Failed to parse library cache metadata:', e);
  }
  return { keys: [], timestamps: {} };
};

const saveCacheMetadata = (metadata: CacheMetadata) => {
  try {
    localStorage.setItem(getCacheMetadataKey(), JSON.stringify(metadata));
  } catch (e) {
    console.warn('Failed to save library cache metadata:', e);
  }
};

const evictOldestCaches = (metadata: CacheMetadata): CacheMetadata => {
  if (metadata.keys.length <= MAX_CACHED_FOLDERS) {
    return metadata;
  }

  // Sort keys by timestamp (oldest first)
  const sortedKeys = [...metadata.keys].sort(
    (a, b) => (metadata.timestamps[a] || 0) - (metadata.timestamps[b] || 0),
  );

  // Remove oldest entries until we're under the limit
  const keysToRemove = sortedKeys.slice(
    0,
    metadata.keys.length - MAX_CACHED_FOLDERS,
  );

  keysToRemove.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore removal errors
    }
  });

  const remainingKeys = metadata.keys.filter((k) => !keysToRemove.includes(k));
  const newTimestamps: { [key: string]: number } = {};
  remainingKeys.forEach((k) => {
    if (metadata.timestamps[k]) {
      newTimestamps[k] = metadata.timestamps[k];
    }
  });

  return { keys: remainingKeys, timestamps: newTimestamps };
};

/**
 * Clear all library cache data
 */
export const clearAllLibraryCache = () => {
  try {
    const metadata = getCacheMetadata();
    metadata.keys.forEach((key) => {
      localStorage.removeItem(key);
    });
    localStorage.removeItem(getCacheMetadataKey());
    localStorage.removeItem(FOLDERS_CACHE_KEY);
  } catch (e) {
    console.warn('Failed to clear library cache:', e);
  }
};

/**
 * Save files data to cache for a specific team/folder
 */
export const saveFilesToCache = (
  teamId: string,
  folderId: string,
  data: {
    files: FileType[];
    pagination: Pagination;
    librarySize: number;
  },
) => {
  if (!teamId || !folderId || !data.files.length) {
    return;
  }

  const cacheKey = getCacheKey(teamId, folderId);
  const cachedData: CachedFilesData = {
    ...data,
    timestamp: Date.now(),
  };

  try {
    localStorage.setItem(cacheKey, JSON.stringify(cachedData));

    // Update metadata
    let metadata = getCacheMetadata();
    if (!metadata.keys.includes(cacheKey)) {
      metadata.keys.push(cacheKey);
    }
    metadata.timestamps[cacheKey] = Date.now();

    // Evict old caches if needed
    metadata = evictOldestCaches(metadata);
    saveCacheMetadata(metadata);
  } catch (e) {
    // If localStorage is full, try to clear old caches and retry
    if (
      e instanceof DOMException &&
      (e.name === 'QuotaExceededError' || e.code === 22)
    ) {
      console.warn('localStorage quota exceeded, clearing old library caches');
      clearAllLibraryCache();
    } else {
      console.warn('Failed to save library files to cache:', e);
    }
  }
};

/**
 * Load files data from cache for a specific team/folder
 * Returns null if cache is missing or expired
 */
export const loadFilesFromCache = (
  teamId: string,
  folderId: string,
): CachedFilesData | null => {
  if (!teamId || !folderId) {
    return null;
  }

  const cacheKey = getCacheKey(teamId, folderId);

  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) {
      return null;
    }

    const data: CachedFilesData = JSON.parse(cached);

    // Check if cache is expired
    if (Date.now() - data.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (e) {
    console.warn('Failed to load library files from cache:', e);
    return null;
  }
};

/**
 * Save folders data to cache
 */
export const saveFoldersToCache = (folders: FolderType[]) => {
  if (!folders.length) {
    return;
  }

  const cachedData: CachedFoldersData = {
    folders,
    timestamp: Date.now(),
  };

  try {
    localStorage.setItem(FOLDERS_CACHE_KEY, JSON.stringify(cachedData));
  } catch (e) {
    console.warn('Failed to save folders to cache:', e);
  }
};

/**
 * Load folders data from cache
 * Returns null if cache is missing or expired
 */
export const loadFoldersFromCache = (): FolderType[] | null => {
  try {
    const cached = localStorage.getItem(FOLDERS_CACHE_KEY);
    if (!cached) {
      return null;
    }

    const data: CachedFoldersData = JSON.parse(cached);

    // Check if cache is expired
    if (Date.now() - data.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(FOLDERS_CACHE_KEY);
      return null;
    }

    return data.folders;
  } catch (e) {
    console.warn('Failed to load folders from cache:', e);
    return null;
  }
};

/**
 * Invalidate cache for a specific folder (e.g., after file operations)
 */
export const invalidateFolderCache = (teamId: string, folderId: string) => {
  if (!teamId || !folderId) {
    return;
  }

  const cacheKey = getCacheKey(teamId, folderId);

  try {
    localStorage.removeItem(cacheKey);

    // Update metadata
    const metadata = getCacheMetadata();
    metadata.keys = metadata.keys.filter((k) => k !== cacheKey);
    delete metadata.timestamps[cacheKey];
    saveCacheMetadata(metadata);
  } catch (e) {
    console.warn('Failed to invalidate folder cache:', e);
  }
};
